import { ActionReducer, MetaReducer, INIT, UPDATE, Action } from '@ngrx/store';
import { AppState } from '../types/app.state';
import {
  DonationFlowState,
  isDonationType,
} from '../types/donation-flow.models';
import { DonationFlowUiActions } from '../state/donation-flow/actions';

const STORAGE_KEY = 'donationFlow';

/**
 * Only fields the flow can safely resume from a page reload live here.
 * Payment Element state, fees, SetupIntent, subscription/invoice, errors
 * and any transient loading flag are intentionally excluded because Stripe
 * artifacts expire and mixing them across sessions/donation types is unsafe.
 */
type PersistedShape = Pick<
  DonationFlowState,
  'priceDraft' | 'customer' | 'metadata' | 'donationType' | 'feesCovered'
>;

function load(): Partial<PersistedShape> | null {
  try {
    const json = localStorage.getItem(STORAGE_KEY);
    return json ? (JSON.parse(json) as Partial<PersistedShape>) : null;
  } catch {
    return null;
  }
}

function save(state: DonationFlowState) {
  const partial: PersistedShape = {
    priceDraft: state.priceDraft,
    customer: state.customer,
    metadata: state.metadata,
    donationType: state.donationType,
    feesCovered: state.feesCovered,
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(partial));
  } catch {
    // ignore
  }
}

function clear() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Validate every persisted field before letting it into the store.
 * A malformed `donationType` invalidates every dependent field (draft +
 * metadata carry the old type), so we drop them together.
 */
function sanitize(raw: Partial<PersistedShape>): Partial<PersistedShape> {
  const out: Partial<PersistedShape> = {};

  const donationType = isDonationType(raw.donationType)
    ? raw.donationType
    : null;

  if (donationType) {
    out.donationType = donationType;
    if (raw.metadata && raw.metadata.donationType === donationType) {
      out.metadata = raw.metadata;
    }
    if (raw.priceDraft) {
      out.priceDraft = raw.priceDraft;
    }
  }

  if (raw.customer && typeof raw.customer === 'object') {
    out.customer = raw.customer;
  }
  if (typeof raw.feesCovered === 'boolean') {
    out.feesCovered = raw.feesCovered;
  }

  return out;
}

export const donationFlowPersistMetaReducer: MetaReducer<AppState> =
  (reducer: ActionReducer<AppState>): ActionReducer<AppState> =>
  (state: AppState | undefined, action: Action): AppState => {
    if (action.type === INIT || action.type === UPDATE) {
      const raw = load();
      if (raw) {
        const base = reducer(undefined, action);
        const clean = sanitize(raw);

        return {
          ...base,
          donationFlow: {
            ...base.donationFlow,
            ...clean,
          },
        };
      }
    }

    const next = reducer(state, action);

    if (action.type === DonationFlowUiActions.reset.type) {
      clear();
      return next;
    }

    if (next?.donationFlow) {
      save(next.donationFlow);
    }

    return next;
  };
